import { Breadcrumbs, Card } from '../../components';
import useDocumentHead from '../../hooks/useDocumentHead';
import { useLang } from '../../i18n';
import './LegalPage.scss';

export type LegalKind = 'privacy' | 'terms' | 'cookies' | 'security' | 'dpa';

export interface LegalPageProps {
  kind: LegalKind;
}

const UPDATED = '2026-05-11';
const MAX_SECTIONS = 20;

export default function LegalPage({ kind }: LegalPageProps) {
  const { t } = useLang();
  const title = t(`legal.${kind}.title`);
  const lead = t(`legal.${kind}.lead`);
  const hasLead = lead !== `legal.${kind}.lead`;

  const sections: { t: string; d: string }[] = [];
  for (let i = 1; i <= MAX_SECTIONS; i += 1) {
    const tKey = `legal.${kind}.s${i}.t`;
    const dKey = `legal.${kind}.s${i}.d`;
    const heading = t(tKey);
    const body = t(dKey);
    if (heading === tKey || body === dKey) break;
    sections.push({ t: heading, d: body });
  }

  useDocumentHead({
    title: `${title} — Ukam IT`,
    description: hasLead ? lead : title,
    canonical: `https://it.ukam.uz/legal/${kind}`,
    og: { title, description: hasLead ? lead : title, type: 'article' },
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ukam IT', item: 'https://it.ukam.uz/' },
        { '@type': 'ListItem', position: 2, name: title, item: `https://it.ukam.uz/legal/${kind}` },
      ],
    },
  });

  return (
    <div className="legal-page">
      <div className="container legal-page__head">
        <Breadcrumbs items={[{ label: 'Ukam IT', href: '/' }, { label: title }]} />
        <h1 className="legal-page__title">{title}</h1>
        <div className="legal-page__updated">
          {t('legal.updated')}: {UPDATED}
        </div>
      </div>
      <section className="container section legal-page__body">
        <Card variant="bordered" padding="lg">
          <div className="legal-page__prose">
            {hasLead ? <p className="legal-page__lead">{lead}</p> : null}
            {sections.map((s, idx) => (
              <div key={`${kind}-s-${idx}`} className="legal-page__section">
                <h2>{s.t}</h2>
                {s.d.split('\n').filter(Boolean).map((para, pi) => (
                  <p key={pi}>{para}</p>
                ))}
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
}
